import SharedMessagesV2 from "@/components/shared/MessagesV2";
import { useUserRegion } from "@/hooks/useUserRegion";
import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { useLanguage } from "@/contexts/LanguageContext";
import { db } from "@/lib/firebase";
import { 
  collection, 
  query, 
  where, 
  getDocs, 
  addDoc, 
  deleteDoc, 
  doc,
  serverTimestamp,
  orderBy,
  onSnapshot
} from "firebase/firestore";
import { BuyerSidebar } from "@/components/layout/BuyerSidebar";
import { DashboardHeader } from "@/components/layout/DashboardHeader";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { useToast } from "@/hooks/use-toast";
import { LanguageSelector } from "@/components/ui/language-selector";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Textarea } from "@/components/ui/textarea";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import { PartnerAssignmentsView } from "@/components/partner-assignments";
import { ConsultantsTab, PartnersTab, ExpertsTab, CalendarTab, type Consultant as SharedConsultant, type Partner as SharedPartner, type Expert } from "@/components/dashboard/shared";
import { CompactCalendarWidget } from "@/components/dashboard/CompactCalendarWidget";
import { SubmitDealForm } from "@/components/submit-deal/SubmitDealForm";
import { SubscriptionGate } from "@/components/SubscriptionGate";
import { EmbeddedConsultantDirectory } from "@/components/dashboard/buyer/EmbeddedConsultantDirectory";
import { BuyerDocumentsList } from "@/components/dashboard/buyer/BuyerDocumentsList";
import { StarredOffersPanel } from "@/components/financing/StarredOffersPanel";
import { BuyerContactRequests } from "@/components/dashboard/ContactRequestsTracker";
import { getListingById, getListingBySlug } from "@/data/listing-details.data";
import { LISTING_DETAILS } from "@/data/listing-details.data";
import { mergeDemoBuyerPartners } from "@/data/demo-partners";
import { useSubscription } from "@/hooks/useSubscription";
import { DealPipelineStepper } from "@/components/buyer/DealPipelineStepper";
import { 
  FileText, 
  Heart, 
  Users, 
  Building2, 
  DollarSign, 
  MapPin,
  TrendingUp,
  Eye,
  Send,
  UserPlus,
  Shield,
  CheckCircle,
  Clock,
  XCircle,
  Download,
  Plus,
  Trash2,
  Search,
  Briefcase,
  Loader2,
  MessageSquare,
  FolderOpen,
  Upload,
  Mail,
  Phone
} from "lucide-react";
import { AddConsultantModal } from "@/components/buyer/AddConsultantModal";
import { AddPartnerModal } from "@/components/buyer/AddPartnerModal";
import BuyerProfile from "@/pages/buyer/BuyerProfile";
import { DealFinancingCalculator } from "@/components/buyer/DealFinancingCalculator";
import { OnboardingGuide } from '@/components/onboarding/OnboardingGuide';


// Types
interface Deal {
  id: string;
  businessName: string;
  businessDescription: string;
  industry: string;
  sector: string;
  askingPrice: number;
  currency: string;
  annualRevenue?: number;
  netProfit?: number;
  city: string;
  state: string;
  country: string;
  sellerId: string;
  sellerName?: string;
  status: 'active' | 'pending' | 'sold';
  createdAt: any;
  interestedBuyers?: string[];
  ndaSigned?: string[];
}

interface NDARequest {
  id: string;
  dealId: string;
  dealName: string;
  buyerId: string;
  sellerId: string;
  status: 'pending' | 'approved' | 'signed' | 'rejected';
  requestedAt: any;
  signedAt?: any;
  documentUrl?: string;
}

export interface Consultant {
  id: string;
  name: string;
  company: string;
  specialty: string;
  email: string;
  phone: string;
  hourlyRate?: number;
  currency?: string;
  bio?: string;
  addedAt: any;
  status: 'available' | 'engaged' | 'inactive';
}

export interface Partner {
  id: string;
  name: string;
  company?: string;
  type: 'internal' | 'external';
  role?: string;
  email: string;
  phone?: string;
  specialty?: string;
  yearsExperience?: number;
  description?: string;
  documentUrl?: string;
  dealIds?: string[];
  addedAt: any;
  isVerified: boolean;
}

interface LOISubmission {
  id: string;
  dealId: string;
  dealName: string;
  buyerId: string;
  sellerId: string;
  purchasePrice: number;
  currency: string;
  status: 'draft' | 'sent' | 'reviewed' | 'accepted' | 'rejected' | 'negotiating';
  submittedAt: any;
  documentUrl: string;
}

// Language translations
const translations = {
  en: {
    title: "Buyer Dashboard",
    subtitle: "Manage your acquisition journey",
    loading: "Loading dashboard...",
    totalDeals: "Total Deals",
    favorites: "Favorites",
    ndaRequests: "NDA Requests",
    loiSubmitted: "LOI Submitted",
    overview: "Overview",
    allDeals: "All Deals",
    ndaStatus: "NDA Status",
    consultants: "Consultants",
    partners: "Partners",
    recentFavorites: "Recent Favorites",
    recentFavoritesDesc: "Your most recently added deals",
    noFavorites: "No favorites yet",
    recentNDA: "Recent NDA Requests",
    recentNDADesc: "Track your NDA status",
    noNDA: "No NDA requests yet",
    quickActions: "Quick Actions",
    exploreDeals: "Explore Deals",
    addConsultant: "Add Consultant",
    addPartner: "Add Partner",
    searchDeals: "Search deals...",
    noDealsFound: "No deals found",
    myFavoriteDeals: "My Favorite Deals",
    favoriteDealsDesc: "Deals you've marked as favorites",
    ndaRequestsStatus: "NDA Requests & Status",
    ndaStatusDesc: "Track your confidentiality agreements",
    requestNDADesc: "Request an NDA to access detailed business information",
    myConsultants: "My Consultants",
    consultantsDesc: "Manage your professional advisors",
    noConsultants: "No consultants added yet",
    addFirstConsultant: "Add Your First Consultant",
    myPartners: "My Partners",
    partnersDesc: "Internal and external business partners",
    noPartners: "No partners added yet",
    addFirstPartner: "Add Your First Partner",
    internalPartners: "Internal Partners",
    externalPartners: "External Partners",
    askingPrice: "Asking Price",
    industry: "Industry",
    location: "Location",
    revenue: "Revenue",
    requestNDA: "Request NDA",
    createLOI: "Create LOI",
    viewDetails: "View Details",
    view: "View",
    signNDA: "Sign NDA",
    downloadNDA: "Download NDA",
    signNow: "Sign Now",
    ndaPending: "NDA Request Pending",
    ndaApproved: "NDA Approved - Ready to Sign",
    ndaSigned: "NDA Signed",
    ndaRejected: "NDA Request Rejected",
    waitingApproval: "Waiting for seller approval...",
    ndaApprovedMsg: "Your NDA has been approved. Please review and sign.",
    ndaSignedOn: "NDA signed on",
    ndaRejectedMsg: "The seller has declined your NDA request.",
    specialty: "Specialty",
    contact: "Contact",
    rate: "Rate",
    role: "Role",
    verified: "Verified",
    requested: "Requested",
    removed: "Removed",
    added: "Added",
    error: "Error",
    failedLoad: "Failed to load dashboard data",
    failedUpdate: "Failed to update",
    removedFromFavorites: "Removed from favorites",
    addedToFavorites: "Added to favorites",
    ndaRequestSent: "NDA Request Sent",
    ndaRequestSentDesc: "The seller will review your NDA request",
    consultantAdded: "Consultant added successfully",
    consultantRemoved: "Consultant removed",
    partnerAdded: "Partner added successfully",
    partnerRemoved: "Partner removed",
    // Messages
    messagesTab: "Messages",
    noMessages: "No messages yet",
    startConversation: "Start a conversation",
    typeMessage: "Type your message...",
    sendMessage: "Send",
    // Documents
    documentsTab: "Documents",
    noDocuments: "No documents yet",
    uploadDocument: "Upload Document",
    contracts: "Contracts",
    agreements: "Agreements",
    financials: "Financials",
    other: "Other",
    // Experts
    ourExperts: "Our Experts",
    expertAssignments: "Expert Assignments",
    partnerNetwork: "Partner Network",
    internalExperts: "Internal Experts",
    externalConsultants: "External Consultants",
    comingSoon: "Coming soon...",
    financingCalc: "Financing Calculator"
  },
  fr: {
    title: "Tableau de bord Acheteur",
    subtitle: "Gérez votre parcours d'acquisition",
    loading: "Chargement du tableau de bord...",
    totalDeals: "Total des offres",
    favorites: "Favoris",
    ndaRequests: "Demandes NDA",
    loiSubmitted: "LOI Soumises",
    overview: "Aperçu",
    allDeals: "Toutes les offres",
    ndaStatus: "Statut NDA",
    consultants: "Consultants",
    partners: "Partenaires",
    recentFavorites: "Favoris récents",
    recentFavoritesDesc: "Vos offres récemment ajoutées",
    noFavorites: "Aucun favori pour le moment",
    recentNDA: "Demandes NDA récentes",
    recentNDADesc: "Suivez le statut de vos NDA",
    noNDA: "Aucune demande NDA pour le moment",
    quickActions: "Actions rapides",
    exploreDeals: "Explorer les offres",
    addConsultant: "Ajouter un consultant",
    addPartner: "Ajouter un partenaire",
    searchDeals: "Rechercher des offres...",
    noDealsFound: "Aucune offre trouvée",
    myFavoriteDeals: "Mes offres favorites",
    favoriteDealsDesc: "Les offres que vous avez marquées comme favorites",
    ndaRequestsStatus: "Demandes et statut NDA",
    ndaStatusDesc: "Suivez vos accords de confidentialité",
    requestNDADesc: "Demandez un NDA pour accéder aux informations détaillées",
    myConsultants: "Mes consultants",
    consultantsDesc: "Gérez vos conseillers professionnels",
    noConsultants: "Aucun consultant ajouté",
    addFirstConsultant: "Ajouter votre premier consultant",
    myPartners: "Mes partenaires",
    partnersDesc: "Partenaires internes et externes",
    noPartners: "Aucun partenaire ajouté",
    addFirstPartner: "Ajouter votre premier partenaire",
    internalPartners: "Partenaires internes",
    externalPartners: "Partenaires externes",
    askingPrice: "Prix demandé",
    industry: "Industrie",
    location: "Emplacement",
    revenue: "Revenus",
    requestNDA: "Demander NDA",
    createLOI: "Créer LOI",
    viewDetails: "Voir détails",
    view: "Voir",
    signNDA: "Signer NDA",
    downloadNDA: "Télécharger NDA",
    signNow: "Signer maintenant",
    ndaPending: "Demande NDA en attente",
    ndaApproved: "NDA Approuvé - Prêt à signer",
    ndaSigned: "NDA Signé",
    ndaRejected: "Demande NDA Rejetée",
    waitingApproval: "En attente d'approbation du vendeur...",
    ndaApprovedMsg: "Votre NDA a été approuvé. Veuillez le réviser et le signer.",
    ndaSignedOn: "NDA signé le",
    ndaRejectedMsg: "Le vendeur a refusé votre demande NDA.",
    specialty: "Spécialité",
    contact: "Contact",
    rate: "Tarif",
    role: "Rôle",
    verified: "Vérifié",
    requested: "Demandé",
    removed: "Supprimé",
    added: "Ajouté",
    error: "Erreur",
    failedLoad: "Échec du chargement des données",
    failedUpdate: "Échec de la mise à jour",
    removedFromFavorites: "Retiré des favoris",
    addedToFavorites: "Ajouté aux favoris",
    ndaRequestSent: "Demande NDA envoyée",
    ndaRequestSentDesc: "Le vendeur examinera votre demande NDA",
    consultantAdded: "Consultant ajouté avec succès",
    consultantRemoved: "Consultant supprimé",
    partnerAdded: "Partenaire ajouté avec succès",
    partnerRemoved: "Partenaire supprimé",
    // Messages
    messagesTab: "Messages",
    noMessages: "Aucun message pour le moment",
    startConversation: "Démarrer une conversation",
    typeMessage: "Tapez votre message...",
    sendMessage: "Envoyer",
    // Documents
    documentsTab: "Documents",
    noDocuments: "Aucun document pour le moment",
    uploadDocument: "Téléverser un document",
    contracts: "Contrats",
    agreements: "Ententes",
    financials: "Documents financiers",
    other: "Autres",
    // Experts
    ourExperts: "Nos experts",
    expertAssignments: "Affectations d'experts",
    partnerNetwork: "Réseau de partenaires",
    internalExperts: "Experts internes",
    externalConsultants: "Consultants externes",
    comingSoon: "Bientôt disponible...",
    financingCalc: "Calculateur de Financement"
  }
};

export default function BuyerDashboard() {
  const { user } = useAuth();
  const userRegion = useUserRegion(user?.id);

  const { language } = useLanguage();
  const navigate = useNavigate();
  const { toast } = useToast();
  const { tier: subscriptionTier, loading: subscriptionLoading } = useSubscription();

  // Use global language context
  const t = translations[language as keyof typeof translations] || translations.en;

  // State management
  const [activeTab, setActiveTab] = useState("overview");
  const [deals, setDeals] = useState<Deal[]>([]);
  const [favorites, setFavorites] = useState<string[]>([]);
  const [ndaRequests, setNdaRequests] = useState<NDARequest[]>([]);
  const [consultants, setConsultants] = useState<Consultant[]>([]);
  const [partners, setPartners] = useState<Partner[]>([]);
  const [experts, setExperts] = useState<Expert[]>([]);
  const [loiSubmissions, setLOISubmissions] = useState<LOISubmission[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [consultantSearch, setConsultantSearch] = useState("");
  const [consultantFilter, setConsultantFilter] = useState<"all" | "available" | "engaged" | "inactive">("all");
  const [partnerSearch, setPartnerSearch] = useState("");
  const [partnerFilter, setPartnerFilter] = useState<"all" | "internal" | "external" | "favorites">("all");
  const [favoriteConsultants, setFavoriteConsultants] = useState<string[]>([]);
  const [expertSearchTerm, setExpertSearchTerm] = useState("");
  const [expertRegionFilter, setExpertRegionFilter] = useState("");

  // Modal states
  const [showAddConsultant, setShowAddConsultant] = useState(false);
  const [showAddPartner, setShowAddPartner] = useState(false);

  const normalizeFavoriteId = (value?: string | null) => {
    if (!value) return null;
    // Try to resolve to listing ID first, then slug, then return raw value
    const bySlug = getListingBySlug(value);
    if (bySlug) return bySlug.id;
    const byId = getListingById(value);
    if (byId) return byId.id;
    return value;
  };

  const getFavoriteStorageKey = (dealId: string) => {
    return getListingById(dealId)?.slug || getListingBySlug(dealId)?.slug || dealId;
  };

  // Load data
  useEffect(() => {
    if (user) {
      loadDashboardData();
    }
  }, [user]);

  useEffect(() => {
    if (!user) {
      setFavorites([]);
      return;
    }

    const favoritesRef = collection(db, "favorites");
    const favoritesQuery = query(favoritesRef, where("buyerId", "==", user.id));

    const unsubscribe = onSnapshot(favoritesQuery, (snapshot) => {
      const favoriteIds = Array.from(
        new Set(
          snapshot.docs
            .map((docSnapshot) => {
              const data = docSnapshot.data();
              return normalizeFavoriteId(data.listingSlug || data.dealId || null);
            })
            .filter((value): value is string => Boolean(value))
        )
      );

      setFavorites(favoriteIds);
    }, (error) => {
      console.error("Error syncing favorites:", error);
    });

    return () => unsubscribe();
  }, [user]);

  const loadDashboardData = async () => {
    if (!user) return;
    
    setIsLoading(true);
    try {
     // Load all deals visible on the public Deal Monitor, from
      // dealSubmissions (source de vérité réelle du currentStage — le
      // miroir 'deals' n'est jamais mis à jour après transitionStage()).
      const dealsRef = collection(db, "dealSubmissions");
      const dealsQuery = query(dealsRef, where("currentStage", "in", [
        'published', 'lender_submission', 'nda_signed',
        'buyer_contact', 'first_meeting', 'loi_signed',
      ]));
      const dealsSnapshot = await getDocs(dealsQuery);
      const dealsData = dealsSnapshot.docs.map(doc => {
        const d = doc.data() as any;
        const raw = d.rawFormData || {};
        const general = raw.general || {};
        const financials = raw.financials || {};
        return {
          id: doc.id,
          businessName: general.legalName || d.legalName || d.businessName || 'Business',
          businessDescription: general.description || d.description || '',
          industry: general.industry || d.industry || '',
          sector: general.industry || d.industry || '',
          askingPrice: financials.askingPrice ?? d.askingPrice ?? 0,
          currency: financials.currency || 'USD',
          city: general.locationCity || d.locationCity || '',
          state: '',
          country: general.locationCountry || d.locationCountry || '',
          sellerId: d.submittedBy || d.sellerId || '',
          status: 'active' as const,
          createdAt: d.createdAt,
        };
      }) as Deal[];
      setDeals(dealsData);

      // Load NDA requests
      const ndaRef = collection(db, "ndaRequests");
      const ndaQuery = query(ndaRef, where("buyerId", "==", user.id));
      const ndaSnapshot = await getDocs(ndaQuery);
      const ndaData = ndaSnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      })) as NDARequest[];

      // Generate demo NDA entries for static listings in favorites
      // This provides test data to validate the NDA tab and Contact Seller flow
      const demoNdaStatuses: Array<NDARequest['status']> = ['signed', 'approved', 'pending', 'signed', 'pending', 'signed'];
      const demoNdas: NDARequest[] = LISTING_DETAILS.map((listing, index) => {
        const content = listing[language as 'en' | 'fr'] || listing.en;
        const status = demoNdaStatuses[index % demoNdaStatuses.length];
        return {
          id: `demo-nda-${listing.id}`,
          dealId: listing.id,
          dealName: content.title,
          buyerId: user.id,
          sellerId: listing.en.broker?.name || listing.en.seller?.name || 'AcquizGroup',
          status,
          requestedAt: { toDate: () => new Date(Date.now() - (index + 1) * 86400000 * 3) },
          signedAt: status === 'signed' ? { toDate: () => new Date(Date.now() - index * 86400000) } : undefined,
          documentUrl: status === 'signed' ? `/tools/nda-generator?deal=${listing.slug}` : undefined,
        };
      });

      // Merge real NDA data with demo data (real takes priority)
      const realDealIds = new Set(ndaData.map(n => n.dealId));
      const mergedNdas = [
        ...ndaData,
        ...demoNdas.filter(demo => !realDealIds.has(demo.dealId)),
      ];
      setNdaRequests(mergedNdas);

      // Load consultants
      const consultantsRef = collection(db, "buyerConsultants");
      const consultantsQuery = query(consultantsRef, where("buyerId", "==", user.id));
      const consultantsSnapshot = await getDocs(consultantsQuery);
      const consultantsData = consultantsSnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      })) as Consultant[];
      setConsultants(consultantsData);

      // Load partners
      const partnersRef = collection(db, "buyerPartners");
      const partnersQuery = query(partnersRef, where("buyerId", "==", user.id));
      const partnersSnapshot = await getDocs(partnersQuery);
      const partnersData = partnersSnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      })) as Partner[];

      setPartners(mergeDemoBuyerPartners(partnersData));

      // Load AcquizGroup experts
      const expertsRef = collection(db, "platformExperts");
      const expertsQuery = query(expertsRef, where("isActive", "==", true));
      const expertsSnapshot = await getDocs(expertsQuery);
      const expertsData = expertsSnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      })) as Expert[];
      setExperts(expertsData);

      // Load LOI submissions
      const loiRef = collection(db, "lois");
      const loiQuery = query(loiRef, where("userId", "==", user.id));
      const loiSnapshot = await getDocs(loiQuery);
      const loiData = loiSnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      })) as LOISubmission[];
      setLOISubmissions(loiData);

    } catch (error) {
      console.error("Error loading dashboard data:", error);
      toast({
        title: t.error,
        description: t.failedLoad,
        variant: "destructive"
      });
    } finally {
      setIsLoading(false);
    }
  };

  // Filtered consultants
  const filteredConsultants = consultants.filter((c) => {
    const matchesSearch = !consultantSearch.trim() ||
      c.name.toLowerCase().includes(consultantSearch.toLowerCase()) ||
      c.specialty.toLowerCase().includes(consultantSearch.toLowerCase()) ||
      c.company?.toLowerCase().includes(consultantSearch.toLowerCase());
    
    const matchesFilter = consultantFilter === "all" || c.status === consultantFilter;
    
    return matchesSearch && matchesFilter;
  });

  // Filtered partners
  const filteredPartners = partners.filter((p) => {
    const matchesSearch = !partnerSearch.trim() ||
      p.name.toLowerCase().includes(partnerSearch.toLowerCase()) ||
      p.role.toLowerCase().includes(partnerSearch.toLowerCase()) ||
      p.company?.toLowerCase().includes(partnerSearch.toLowerCase());
    
    const matchesFilter = partnerFilter === "all" || p.type === partnerFilter;
    
    return matchesSearch && matchesFilter;
  });

  // Favorites management
  const toggleFavorite = async (dealId: string) => {
    if (!user) return;

    try {
      const storageKey = getFavoriteStorageKey(dealId);

      if (favorites.includes(dealId)) {
        const favRef = collection(db, "favorites");
        const q = query(favRef, where("buyerId", "==", user.id));
        const snapshot = await getDocs(q);

        const allDocs = snapshot.docs.filter((docSnap) => {
          const data = docSnap.data();
          const savedSlug = data.listingSlug ? getFavoriteStorageKey(normalizeFavoriteId(data.listingSlug) || data.listingSlug) : null;
          const savedDealId = normalizeFavoriteId(data.dealId);

          return savedSlug === storageKey || savedDealId === dealId;
        });

        await Promise.all(allDocs.map(docSnap => deleteDoc(docSnap.ref)));
        
        setFavorites(prev => prev.filter(id => id !== dealId));
        toast({ title: t.removedFromFavorites });
      } else {
        await addDoc(collection(db, "favorites"), {
          buyerId: user.id,
          listingSlug: storageKey,
          dealId,
          addedAt: serverTimestamp()
        });
        
        setFavorites(prev => [...prev, dealId]);
        toast({ title: t.addedToFavorites });
      }
    } catch (error) {
      console.error("Error toggling favorite:", error);
      toast({
        title: t.error,
        description: t.failedUpdate,
        variant: "destructive"
      });
    }
  };

  // NDA management
  const requestNDA = async (deal: Deal) => {
    if (!user) return;

    if (subscriptionLoading) {
      toast({
        title: language === 'fr' ? 'Vérification du forfait' : 'Checking subscription',
        description: language === 'fr' ? 'Veuillez réessayer dans un instant.' : 'Please try again in a moment.',
      });
      return;
    }

    if (!subscriptionTier || subscriptionTier === 'visitor') {
      navigate('/buyer/payment');
      return;
    }

    try {
      await addDoc(collection(db, "ndaRequests"), {
        dealId: deal.id,
        dealName: deal.businessName,
        buyerId: user.id,
        buyerName: user.displayName || user.email,
        sellerId: deal.sellerId,
        status: 'pending',
        requestedAt: serverTimestamp()
      });

      toast({
        title: t.ndaRequestSent,
        description: t.ndaRequestSentDesc
      });

      await loadDashboardData();
    } catch (error) {
      console.error("Error requesting NDA:", error);
      toast({
        title: t.error,
        description: t.failedUpdate,
        variant: "destructive"
      });
    }
  };

  // Consultant management
  const addConsultant = async (consultantData: Partial<Consultant>) => {
    if (!user) return;

    try {
      await addDoc(collection(db, "buyerConsultants"), {
        ...consultantData,
        buyerId: user.id,
        addedAt: serverTimestamp(),
        status: 'available'
      });

      toast({ title: t.consultantAdded });
      setShowAddConsultant(false);
      await loadDashboardData();
    } catch (error) {
      console.error("Error adding consultant:", error);
      toast({
        title: t.error,
        description: t.failedUpdate,
        variant: "destructive"
      });
    }
  };

  const removeConsultant = async (consultantId: string) => {
    try {
      await deleteDoc(doc(db, "buyerConsultants", consultantId));
      toast({ title: t.consultantRemoved });
      await loadDashboardData();
    } catch (error) {
      console.error("Error removing consultant:", error);
      toast({
        title: t.error,
        description: t.failedUpdate,
        variant: "destructive"
      });
    }
  };

  // Consultant favorites management - persisted in Firestore
  const toggleConsultantFavorite = async (consultantId: string) => {
    if (!user) return;
    
    try {
      const favRef = collection(db, "users", user.id, "consultantsFavorites");
      
      if (favoriteConsultants.includes(consultantId)) {
        // Remove from favorites
        const q = query(favRef, where("consultantId", "==", consultantId));
        const snapshot = await getDocs(q);
        snapshot.forEach(async (docSnap) => {
          await deleteDoc(docSnap.ref);
        });
        setFavoriteConsultants(prev => prev.filter(id => id !== consultantId));
        toast({ title: language === 'fr' ? 'Retiré des favoris' : 'Removed from favorites' });
      } else {
        // Add to favorites
        await addDoc(favRef, {
          consultantId,
          addedAt: serverTimestamp()
        });
        setFavoriteConsultants(prev => [...prev, consultantId]);
        toast({ title: language === 'fr' ? 'Ajouté aux favoris' : 'Added to favorites' });
      }
    } catch (error) {
      console.error("Error toggling consultant favorite:", error);
      toast({
        title: t.error,
        description: t.failedUpdate,
        variant: "destructive"
      });
    }
  };

  // Load consultant favorites from Firestore
  useEffect(() => {
    if (!user) return;
    
    const loadConsultantFavorites = async () => {
      try {
        const favRef = collection(db, "users", user.id, "consultantsFavorites");
        const snapshot = await getDocs(favRef);
        const ids = snapshot.docs.map(doc => doc.data().consultantId);
        setFavoriteConsultants(ids);
      } catch (error) {
        console.error("Error loading consultant favorites:", error);
      }
    };
    
    loadConsultantFavorites();
  }, [user]);


  // Partner management
  const addPartner = async (partnerData: Partial<Partner> & { documentFile?: File | null }) => {
    if (!user) return;

    try {
      const { documentFile, ...rest } = partnerData;
      let documentUrl: string | undefined;

      if (documentFile) {
        const { ref: storageRef, uploadBytes, getDownloadURL } = await import("firebase/storage");
        const { storage } = await import("@/lib/firebase");
        const safeName = documentFile.name.replace(/[^a-zA-Z0-9._-]/g, "_");
        const fileRef = storageRef(storage, `users/${user.id}/partnerDocs/${Date.now()}_${safeName}`);
        await uploadBytes(fileRef, documentFile);
        documentUrl = await getDownloadURL(fileRef);
      }

      await addDoc(collection(db, "buyerPartners"), {
        ...rest,
        type: rest.type || 'external',
        ...(documentUrl ? { documentUrl } : {}),
        buyerId: user.id,
        addedAt: serverTimestamp(),
        isVerified: false
      });

      toast({ title: t.partnerAdded });
      setShowAddPartner(false);
      await loadDashboardData();
    } catch (error) {
      console.error("Error adding partner:", error);
      toast({
        title: t.error,
        description: t.failedUpdate,
        variant: "destructive"
      });
    }
  };

  const removePartner = async (partnerId: string) => {
    try {
      await deleteDoc(doc(db, "buyerPartners", partnerId));
      toast({ title: t.partnerRemoved });
      await loadDashboardData();
    } catch (error) {
      console.error("Error removing partner:", error);
      toast({
        title: t.error,
        description: t.failedUpdate,
        variant: "destructive"
      });
    }
  };

  // Create LOI
  const createLOI = (deal: Deal) => {
    navigate("/tools/loi-generator", { state: { deal } });
  };

  // Utility functions
  const formatCurrency = (amount: number, currency: string = "USD") => {
    return new Intl.NumberFormat(language === 'fr' ? 'fr-FR' : 'en-US', {
      style: 'currency',
      currency,
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(amount);
  };

  const getNDAStatus = (dealId: string): NDARequest | undefined => {
    return ndaRequests.find(nda => nda.dealId === dealId);
  };

  const filteredDeals = deals.filter(deal => 
    deal.businessName?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    deal.industry?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    deal.city?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const favoriteDeals = favorites
    .map((favoriteId) => {
      const existingDeal = deals.find((deal) => deal.id === favoriteId);
      if (existingDeal) return existingDeal;

      const listing = getListingById(favoriteId) || getListingBySlug(favoriteId);
      if (!listing) return null;

      const listingContent = (listing[language as 'en' | 'fr'] || listing.en);
      const locationParts = listingContent.location.split(',').map((value) => value.trim());
      const parsedPrice = Number(listingContent.financials.askingPrice.replace(/[^\d.]/g, '')) || 0;

      return {
        id: listing.id,
        businessName: listingContent.title,
        businessDescription: listingContent.description?.[0] || '',
        industry: listingContent.detailedInfo.find((item) => /industry|secteur/i.test(item.label))?.value || 'Business',
        sector: listingContent.detailedInfo.find((item) => /industry|secteur/i.test(item.label))?.value || 'Business',
        askingPrice: parsedPrice,
        currency: 'USD',
        city: locationParts[0] || '',
        state: locationParts[1] || '',
        country: locationParts[2] || '',
        sellerId: '__listing__',
        status: 'active' as const,
        createdAt: null,
      };
    })
    .filter(Boolean) as Deal[];

  const getDealDetailPath = (dealId: string) => {
    const listing = getListingById(dealId) || getListingBySlug(dealId);
    return listing ? `/listings/${listing.slug}` : `/deals/${dealId}`;
  };

  // Render Deal Card
  const renderDealCard = (deal: Deal, showActions: boolean = true) => {
    const isFavorite = favorites.includes(deal.id);
    const ndaStatus = getNDAStatus(deal.id);
    const detailPath = getDealDetailPath(deal.id);
    const isStaticListing = deal.sellerId === '__listing__';

    return (
      <Card key={deal.id} className="overflow-hidden hover:shadow-lg transition-shadow cursor-pointer" onClick={() => navigate(detailPath)}>
        <CardHeader className="pb-2">
          <div className="flex justify-between items-start">
            <div>
              <CardTitle
                className="text-lg cursor-pointer hover:text-primary"
                onClick={() => navigate(detailPath)}
              >
                {deal.businessName}
              </CardTitle>
              <CardDescription className="line-clamp-2">
                {deal.businessDescription?.substring(0, 120)}...
              </CardDescription>
            </div>
            <Button
              variant="ghost"
              size="icon"
              onClick={(e) => { e.stopPropagation(); toggleFavorite(deal.id); }}
              className={isFavorite ? "text-red-500 hover:text-red-600" : "text-muted-foreground"}
            >
              <Heart className={`h-5 w-5 ${isFavorite ? "fill-current" : ""}`} />
            </Button>
          </div>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-2 gap-4 mb-4">
            <div className="flex items-center gap-2">
              <DollarSign className="h-4 w-4 text-primary" />
              <div>
                <p className="text-xs text-muted-foreground">{t.askingPrice}</p>
                <p className="font-semibold">{formatCurrency(deal.askingPrice, deal.currency)}</p>
              </div>
            </div>

            <div className="flex items-center gap-2">
              <Building2 className="h-4 w-4 text-primary" />
              <div>
                <p className="text-xs text-muted-foreground">{t.industry}</p>
                <p className="font-medium">{deal.industry}</p>
              </div>
            </div>

            <div className="flex items-center gap-2">
              <MapPin className="h-4 w-4 text-primary" />
              <div>
                <p className="text-xs text-muted-foreground">{t.location}</p>
                <p className="font-medium">{deal.city}, {deal.state}</p>
              </div>
            </div>

            {deal.annualRevenue && (
              <div className="flex items-center gap-2">
                <TrendingUp className="h-4 w-4 text-primary" />
                <div>
                  <p className="text-xs text-muted-foreground">{t.revenue}</p>
                  <p className="font-semibold">{formatCurrency(deal.annualRevenue, deal.currency)}</p>
                </div>
              </div>
            )}
          </div>

          {ndaStatus && (
            <div className="mb-4">
              {ndaStatus.status === 'pending' && (
                <Badge variant="secondary" className="gap-1">
                  <Clock className="h-3 w-3" />
                  {t.ndaPending}
                </Badge>
              )}
              {ndaStatus.status === 'approved' && (
                <Badge variant="default" className="gap-1 bg-blue-500">
                  <CheckCircle className="h-3 w-3" />
                  {t.ndaApproved}
                </Badge>
              )}
              {ndaStatus.status === 'signed' && (
                <Badge variant="default" className="gap-1 bg-green-500">
                  <CheckCircle className="h-3 w-3" />
                  {t.ndaSigned}
                </Badge>
              )}
              {ndaStatus.status === 'rejected' && (
                <Badge variant="destructive" className="gap-1">
                  <XCircle className="h-3 w-3" />
                  {t.ndaRejected}
                </Badge>
              )}
            </div>
          )}

          {showActions && (
            <div className="flex flex-wrap gap-2" onClick={(e) => e.stopPropagation()}>
              {!ndaStatus && (
                <Button size="sm" onClick={() => {
                  if (isStaticListing) {
                    const listing = getListingById(deal.id);
                    const slug = listing?.slug || deal.id;
                    const params = new URLSearchParams({
                      deal: slug,
                      dealName: deal.businessName,
                      industry: deal.industry || '',
                      recipientType: 'buyer',
                    });
                    navigate(`/tools/nda-generator?${params.toString()}`);
                  } else {
                    requestNDA(deal);
                  }
                }}>
                  <Shield className="h-4 w-4 mr-1" />
                  {t.requestNDA}
                </Button>
              )}
              
              {ndaStatus?.status === 'signed' && (
                <Button size="sm" variant="default" onClick={() => createLOI(deal)}>
                  <Send className="h-4 w-4 mr-1" />
                  {t.createLOI}
                </Button>
              )}

              <Button size="sm" variant="outline" onClick={() => navigate(detailPath)}>
                <Eye className="h-4 w-4 mr-1" />
                {t.viewDetails}
              </Button>
            </div>
          )}
        </CardContent>
      </Card>
    );
  };

  if (isLoading) {
    return (
      <div className="flex min-h-screen bg-background">
        <BuyerSidebar activeTab={activeTab} onTabChange={setActiveTab} favoritesCount={favorites.length} />
        <div className="flex-1 flex flex-col">
          <DashboardHeader />
          <main className="flex-1 p-6 bg-muted/30 flex items-center justify-center">
            <div className="text-center">
              <Loader2 className="h-8 w-8 animate-spin mx-auto mb-4 text-primary" />
              <p className="text-muted-foreground">{t.loading}</p>
            </div>
          </main>
        </div>
      </div>
    );
  }

  return (
    <div className="flex min-h-screen bg-background">
      <BuyerSidebar activeTab={activeTab} onTabChange={setActiveTab} favoritesCount={favorites.length} />
      <div className="flex-1 flex flex-col">
        <DashboardHeader />
        <main className="flex-1 p-6 bg-muted/30">
          <div className="space-y-6">
      <div className="space-y-6">
        {/* Header */}
        <div>
          <h1 className="text-3xl font-bold tracking-tight">{t.title}</h1>
          <p className="text-muted-foreground">{t.subtitle}</p>
        </div>

        {/* Quick Stats */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center gap-2">
                <Briefcase className="h-5 w-5 text-primary" />
                <div>
                  <p className="text-sm text-muted-foreground">{t.totalDeals}</p>
                  <p className="text-2xl font-bold">{deals.length}</p>
                </div>
              </div>
            </CardContent>
          </Card>
          
          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center gap-2">
                <Heart className="h-5 w-5 text-red-500" />
                <div>
                  <p className="text-sm text-muted-foreground">{t.favorites}</p>
                  <p className="text-2xl font-bold">{favorites.length}</p>
                </div>
              </div>
            </CardContent>
          </Card>
          
          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center gap-2">
                <FileText className="h-5 w-5 text-blue-500" />
                <div>
                  <p className="text-sm text-muted-foreground">{t.ndaRequests}</p>
                  <p className="text-2xl font-bold">{ndaRequests.length}</p>
                </div>
              </div>
            </CardContent>
          </Card>
          
          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center gap-2">
                <Send className="h-5 w-5 text-green-500" />
                <div>
                  <p className="text-sm text-muted-foreground">{t.loiSubmitted}</p>
                  <p className="text-2xl font-bold">{loiSubmissions.length}</p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Main Tabs - Navigation controlled by sidebar */}
        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <TabsContent value="overview" className="space-y-6">
            <OnboardingGuide
              role="buyer"
              language={language as 'en' | 'fr'}
              onTabChange={setActiveTab}
            />
            {/* Pipeline Progress for Active Deals */}
            {favoriteDeals.length > 0 && (
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Briefcase className="h-5 w-5 text-primary" />
                    {language === 'fr' ? 'Progression du pipeline' : 'Pipeline Progress'}
                  </CardTitle>
                  <CardDescription>
                    {language === 'fr' ? 'Suivez vos dossiers d\'acquisition en cours' : 'Track your active acquisition deals'}
                  </CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                  {favoriteDeals.slice(0, 5).map(deal => {
                    const ndaStatus = getNDAStatus(deal.id);
                    const isStaticListing = deal.sellerId === '__listing__';
                    // Determine pipeline stage based on NDA status
                    let stage = isStaticListing ? 'deal_monitor' : (deal as any).currentStage || 'submission';
                    if (ndaStatus?.status === 'signed') stage = 'nda_signed';
                    else if (ndaStatus?.status === 'approved') stage = 'nda_pending';
                    else if (ndaStatus?.status === 'pending') stage = 'nda_pending';
                    return (
                      <DealPipelineStepper
                        key={deal.id}
                        currentStage={stage}
                        dealName={deal.businessName}
                      />
                    );
                  })}
                </CardContent>
              </Card>
            )}
            <div className="grid md:grid-cols-2 gap-6">
              <Card>
                <CardHeader>
                  <CardTitle>{t.recentFavorites}</CardTitle>
                  <CardDescription>{t.recentFavoritesDesc}</CardDescription>
                </CardHeader>
                <CardContent>
                  {favorites.length === 0 ? (
                    <p className="text-center text-muted-foreground py-4">{t.noFavorites}</p>
                  ) : (
                    <div className="space-y-3">
                      {favoriteDeals
                        .slice(0, 3)
                        .map(deal => (
                          <div key={deal.id} className="flex items-center justify-between p-3 border rounded-lg">
                            <div>
                              <p className="font-medium">{deal.businessName}</p>
                              <p className="text-sm text-muted-foreground">{deal.industry}</p>
                            </div>
                            <Button size="sm" variant="ghost" onClick={() => navigate(getDealDetailPath(deal.id))}>
                              {t.view}
                            </Button>
                          </div>
                        ))}
                    </div>
                  )}
                </CardContent>
              </Card>

              <Card>
                <CardHeader>
                  <CardTitle>{t.recentNDA}</CardTitle>
                  <CardDescription>{t.recentNDADesc}</CardDescription>
                </CardHeader>
                <CardContent>
                  {ndaRequests.length === 0 ? (
                    <p className="text-center text-muted-foreground py-4">{t.noNDA}</p>
                  ) : (
                    <div className="space-y-3">
                      {ndaRequests.slice(0, 3).map(nda => (
                        <div key={nda.id} className="flex items-center justify-between p-3 border rounded-lg">
                          <div>
                            <p className="font-medium">{nda.dealName}</p>
                            <Badge variant="secondary" className="mt-1">{nda.status}</Badge>
                          </div>
                          {nda.status === 'approved' && (
                            <Button size="sm">{t.signNDA}</Button>
                          )}
                        </div>
                      ))}
                    </div>
                  )}
                </CardContent>
              </Card>

              {/* Compact Calendar Widget */}
              <CompactCalendarWidget className="md:col-span-2" />
            </div>

            {/* Starred Financing Offers */}
            <SubscriptionGate requiredTier="essential">
              <StarredOffersPanel />
            </SubscriptionGate>

            <Card>
              <CardHeader>
                <CardTitle>{t.quickActions}</CardTitle>
              </CardHeader>
              <CardContent className="grid grid-cols-3 gap-4">
                <Button onClick={() => setActiveTab("deals")} variant="outline" className="h-24 flex flex-col gap-2">
                  <Briefcase className="h-6 w-6" />
                  <span>{t.exploreDeals}</span>
                </Button>
                
                <Button onClick={() => setShowAddConsultant(true)} variant="outline" className="h-24 flex flex-col gap-2">
                  <UserPlus className="h-6 w-6" />
                  <span>{t.addConsultant}</span>
                </Button>
                
                <Button onClick={() => setShowAddPartner(true)} variant="outline" className="h-24 flex flex-col gap-2">
                  <Users className="h-6 w-6" />
                  <span>{t.addPartner}</span>
                </Button>
              </CardContent>
            </Card>
          </TabsContent>

          {/* All Deals Tab */}
          <TabsContent value="deals">
            {/* Contact Requests Tracker */}
            <div className="mb-6">
              <BuyerContactRequests language={language} />
            </div>
            <div className="mb-6">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                <Input
                  placeholder={t.searchDeals}
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="pl-10"
                />
              </div>
            </div>

            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
              {filteredDeals.map(deal => renderDealCard(deal))}
            </div>

            {filteredDeals.length === 0 && (
              <Card>
                <CardContent className="py-12 text-center">
                  <p className="text-muted-foreground">{t.noDealsFound}</p>
                </CardContent>
              </Card>
            )}
          </TabsContent>

          {/* Favorites Tab */}
          <TabsContent value="favorites">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Heart className="h-5 w-5 text-red-500" />
                  {t.myFavoriteDeals}
                </CardTitle>
                <CardDescription>{t.favoriteDealsDesc}</CardDescription>
              </CardHeader>
              <CardContent>
                {favorites.length === 0 ? (
                  <div className="text-center py-12">
                    <Heart className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
                    <p className="text-muted-foreground mb-4">{t.noFavorites}</p>
                    <Button onClick={() => setActiveTab("deals")}>{t.exploreDeals}</Button>
                  </div>
                ) : (
                    <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
                      {favoriteDeals.map(deal => renderDealCard(deal))}
                    </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* NDA Status Tab */}
          <TabsContent value="nda">
            <SubscriptionGate requiredTier="essential">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <FileText className="h-5 w-5 text-blue-500" />
                  {t.ndaRequestsStatus}
                </CardTitle>
                <CardDescription>{t.ndaStatusDesc}</CardDescription>
              </CardHeader>
              <CardContent>
                {ndaRequests.length === 0 ? (
                  <div className="text-center py-12">
                    <Shield className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
                    <p className="text-muted-foreground mb-2">{t.noNDA}</p>
                    <p className="text-sm text-muted-foreground">{t.requestNDADesc}</p>
                  </div>
                ) : (
                  <div className="space-y-4">
                    {ndaRequests.map(nda => {
                      const deal = deals.find(d => d.id === nda.dealId);
                      return (
                        <Card key={nda.id} className="cursor-pointer hover:shadow-md transition-shadow" onClick={() => navigate(getDealDetailPath((nda as any).dealSlug || nda.dealId))}>
                          <CardHeader className="pb-2">
                            <div className="flex items-center justify-between">
                              <div>
                                <CardTitle className="text-lg hover:text-primary transition-colors">{nda.dealName}</CardTitle>
                                <CardDescription>
                                  {t.requested}: {nda.requestedAt?.toDate?.()?.toLocaleDateString() || 'N/A'}
                                </CardDescription>
                              </div>
                              <Badge variant={
                                nda.status === 'signed' ? 'default' :
                                nda.status === 'approved' ? 'secondary' :
                                nda.status === 'rejected' ? 'destructive' : 'outline'
                              }>
                                {nda.status}
                              </Badge>
                            </div>
                          </CardHeader>
                          <CardContent>
                            {nda.status === 'pending' && (
                              <p className="text-sm text-muted-foreground">{t.waitingApproval}</p>
                            )}
                            
                            {nda.status === 'approved' && (
                              <div className="space-y-3">
                                <p className="text-sm">{t.ndaApprovedMsg}</p>
                                <div className="flex gap-2">
                                  <Button variant="outline" size="sm" onClick={(e) => e.stopPropagation()}>
                                    <Download className="h-4 w-4 mr-1" />
                                    {t.downloadNDA}
                                  </Button>
                                  <Button size="sm" onClick={(e) => e.stopPropagation()}>{t.signNow}</Button>
                                </div>
                              </div>
                            )}
                            
                            {nda.status === 'signed' && (
                              <div className="flex items-center justify-between">
                                <div className="flex items-center gap-2 text-green-600">
                                  <CheckCircle className="h-4 w-4" />
                                  <span className="text-sm">{t.ndaSignedOn} {nda.signedAt?.toDate?.()?.toLocaleDateString() || 'N/A'}</span>
                                </div>
                                {deal && (
                                  <Button size="sm" onClick={(e) => { e.stopPropagation(); createLOI(deal); }}>
                                    <Send className="h-4 w-4 mr-1" />
                                    {t.createLOI}
                                  </Button>
                                )}
                              </div>
                            )}
                            
                            {nda.status === 'rejected' && (
                              <p className="text-sm text-destructive">{t.ndaRejectedMsg}</p>
                            )}
                          </CardContent>
                        </Card>
                      );
                    })}
                  </div>
                )}
              </CardContent>
            </Card>
            </SubscriptionGate>
          </TabsContent>

          {/* Consultants Tab */}
          <TabsContent value="consultants">
            <EmbeddedConsultantDirectory />
          </TabsContent>

          {/* Partners Tab */}
          <TabsContent value="partners">
            <SubscriptionGate requiredTier="essential">
            <PartnersTab
              partners={partners.map(p => ({
                ...p,
                type: p.type as 'internal' | 'external',
                role: p.role || 'Partner'
              }))}
              consultants={consultants.map(c => ({
                ...c,
                specialty: c.specialty || 'General'
              }))}
              favoriteConsultants={favoriteConsultants}
              onAddClick={() => setShowAddPartner(true)}
              onRemove={removePartner}
              onToggleFavorite={toggleConsultantFavorite}
              onViewConsultants={() => setActiveTab('consultants')}
              language={language as 'en' | 'fr'}
              formatCurrency={formatCurrency}
            />
            </SubscriptionGate>
          </TabsContent>

          {/* Messages Tab */}
<TabsContent value="messages" className="space-y-4">
  <SharedMessagesV2 role="buyer" />
</TabsContent>
                
          {/* Documents Tab */}
          <TabsContent value="documents" className="space-y-4">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <FileText className="h-5 w-5" />
                  {t.documentsTab}
                </CardTitle>
              </CardHeader>
              <CardContent>
                <BuyerDocumentsList />
              </CardContent>
            </Card>
          </TabsContent>



          {/* Experts Tab */}
          <TabsContent value="experts" className="space-y-4">
            <SubscriptionGate requiredTier="essential">
              <div className="flex flex-col sm:flex-row gap-3 mb-2">
                <div className="relative flex-1">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                  <Input
                    placeholder={language === 'fr' ? "Rechercher par nom ou spécialité..." : "Search by name or specialty..."}
                    value={expertSearchTerm}
                    onChange={(e) => setExpertSearchTerm(e.target.value)}
                    className="pl-9"
                  />
                </div>
                <Input
                  placeholder={language === 'fr' ? "Filtrer par région (ex: Quebec)" : "Filter by region (e.g., Quebec)"}
                  value={expertRegionFilter}
                  onChange={(e) => setExpertRegionFilter(e.target.value)}
                  className="sm:w-64"
                />
              </div>
              <ExpertsTab
                experts={experts.filter(e => {
                  const q = expertSearchTerm.trim().toLowerCase();
                  const r = expertRegionFilter.trim().toLowerCase();
                  const matchesQ = !q ||
                    e.name?.toLowerCase().includes(q) ||
                    (e.specialty || '').toLowerCase().includes(q) ||
                    (e.title || '').toLowerCase().includes(q);
                  const matchesR = !r ||
                    (e.stateProvince || '').toLowerCase().includes(r) ||
                    (e.serviceRegion || '').toLowerCase().includes(r) ||
                    (e.jurisdictions || []).some(j => j.toLowerCase().includes(r));
                  return matchesQ && matchesR;
                })}
                language={language as 'en' | 'fr'}
                userRegion={userRegion}
                formatCurrency={formatCurrency}
              />
            </SubscriptionGate>
          </TabsContent>


          {/* Financing Calculator Tab */}
          <TabsContent value="financing-calc" className="space-y-4">
            <SubscriptionGate requiredTier="essential">
              <DealFinancingCalculator language={language as 'en' | 'fr'} />
            </SubscriptionGate>
          </TabsContent>

          {/* Calendar Tab */}
          <TabsContent value="calendar" className="space-y-4">
            <CalendarTab showAvailabilitySettings={true} />
          </TabsContent>



          {/* Submit Deal Tab */}
          <TabsContent value="submit-deal" className="space-y-4">
            <SubscriptionGate requiredTier="byod">
              <SubmitDealForm onComplete={() => setActiveTab("overview")} />
            </SubscriptionGate>
          </TabsContent>

          <TabsContent value="profile" className="space-y-4">
            <BuyerProfile />
          </TabsContent>
        </Tabs>
      </div>

      {/* Add Consultant Modal */}
      {showAddConsultant && (
        <AddConsultantModal
          onClose={() => setShowAddConsultant(false)}
          onAdd={addConsultant}
          lang={language}
        />
      )}

      {/* Add Partner Modal */}
      {showAddPartner && (
        <AddPartnerModal
          onClose={() => setShowAddPartner(false)}
          onAdd={addPartner}
          lang={language}
        />
      )}
          </div>
        </main>
      </div>
    </div>
  );
}
